import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MultiSelect } from "./ui/multi-select";
import type { Brand } from "./AppSidebar";
import { ArrowRight, CircleAlert, Tags } from "lucide-react";
import CountriesSelect from "./ui/countries-dropdowm";
import { useNavigate, useOutletContext } from "react-router";
import type { Plan } from "@/services/api";
import { isPromptCountryLimitReached } from "@/lib/countryLimits";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const MIN_WORDS = 3;

const AddPromptsDialog = ({
  currentBrand,
  addMutation,
  clustersData,
  cluster,
  openAddPromptDialog,
  setOpenAddPromptDialog,
}: {
  currentBrand: Brand;
  addMutation: any;
  cluster: any;
  clustersData: any;
  openAddPromptDialog: boolean;
  setOpenAddPromptDialog: (val: boolean) => void;
}) => {
  const { t } = useLocale();
  const { plan, predefinedCountries } = useOutletContext<{
    plan: Plan;
    predefinedCountries: any;
  }>();
  const navigate = useNavigate();
  const [promptCountry, setPromptCountry] = useState(
    currentBrand?.country || "",
  );
  const [promptCluster, setPromptCluster] = useState(cluster);
  const [addPromptError, setAddPromptError] = useState("");
  const [prompt, setPrompt] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const isCountryLimited = isPromptCountryLimitReached({
    plan,
    predefinedCountries,
    promptCountry,
    brandCountry: currentBrand?.country,
  });
  const handlePromptAdd = async () => {
    if ((!prompt.length || !promptCountry.length) && !isCountryLimited) {
      return setAddPromptError("Prompt and country are required");
    }

    if (isCountryLimited) {
      return setAddPromptError(
        `Expand your reach — upgrade to track prompts across more countries`,
      );
    }

    if (
      prompt
        .trim()
        .split(/\s+/)
        .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
        .filter(Boolean).length < MIN_WORDS
    ) {
      return setAddPromptError(`Please use min. 3 words`);
    }

    const selectedCluster = clustersData?.find(
      (cl: { id: string; title: string }) => cl.title === promptCluster,
    );
    const newPrompt = {
      country: promptCountry,
      text: prompt,
      isActive: true,
      brandId: currentBrand.brandId || "",
      clusterId: selectedCluster?.id || null,
      clusterTitle: selectedCluster?.id ? undefined : promptCluster,
    };

    addMutation(newPrompt);

    setOpenAddPromptDialog(false);
    setPrompt("");
    setPromptCountry(currentBrand.country || "");
  };

  useEffect(() => {
    setAddPromptError("");
  }, [openAddPromptDialog, promptCountry]);

  return (
    <Dialog
      open={openAddPromptDialog}
      onOpenChange={(open) => {
        setOpenAddPromptDialog(open);
        setPrompt("");
        setPromptCluster(cluster);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
          onClick={() => setOpenAddPromptDialog(true)}
        >
          <LocalizedText id="legacy.add.prompt.1vpa268" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <LocalizedText id="legacy.add.prompt.1vpa268" />
          </DialogTitle>
          <DialogDescription>
            <LocalizedText id="legacy.track.how.ai.models.respond.to.your.brand.10bdt0i" />
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="relative">
            <textarea
              name="prompt"
              autoComplete="off"
              placeholder={t("legacy.enter.a.prompt.13so9y")}
              value={prompt}
              onChange={(e) => {
                if (e.target.value.length <= 350) {
                  setAddPromptError("");
                  setPrompt(e.target.value);
                } else {
                  setAddPromptError("Prompts name should be max. 350 chars");
                }
              }}
              className="border rounded-md w-full pt-4 px-4 pb-12 resize-none"
            />
            <div className="absolute rounded-md flex w-[97%] justify-end bottom-[8px] left-[2px] bg-white p-1 text-sm text-[#737373] pointer-events-none">
              {prompt.length}/350
            </div>
          </div>
          <div className="flex gap-2 flex-col">
            <label htmlFor="Country" className="text-sm font-medium">
              <LocalizedText id="legacy.country.1oy8cul" />
            </label>
            <CountriesSelect
              isFull={true}
              isIcon={false}
              promptCountry={promptCountry}
              setPromptCountry={setPromptCountry}
            ></CountriesSelect>
          </div>
          {addPromptError && (
            <div className="flex p-4 items-center rounded-md bg-[#DC26261A] gap-2">
              <div className="p-2 rounded-lg bg-[#DC2626] h-fit">
                <CircleAlert size={22} className="text-white" />
              </div>
              <div className="text-xs">{addPromptError}</div>
              {isCountryLimited && (
                <Button
                  onClick={() => navigate(`/dashboard/plans-pricing`)}
                  className="cursor-pointer"
                  variant="outline"
                >
                  <LocalizedText id="legacy.see.plans.ox5kgm" /> <ArrowRight />
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-2 flex-col min-w-0 w-full">
            <label htmlFor="Cluster" className="text-sm font-medium">
              <LocalizedText id="legacy.cluster.1tbl9v3" />
            </label>
            <Select
              value={promptCluster}
              onValueChange={(value) => setPromptCluster(value)}
            >
              <SelectTrigger
                className="text-[#0A0A0A] flex w-full **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
                size="sm"
                aria-label={t("legacy.select.a.value.nv8d47")}
              >
                <SelectValue placeholder={promptCluster} />
              </SelectTrigger>
              <SelectContent className="rounded-xl w-[--radix-select-trigger-width] overflow-hidden">
                {[{ title: "No Clusters" }, ...(clustersData || [])]
                  .filter(
                    (value, index, self) =>
                      self.findIndex(
                        (item: { title: string }) => item.title === value.title,
                      ) === index,
                  )
                  .map((cl: { title: string }) => (
                    <SelectItem
                      key={cl.title}
                      value={cl.title}
                      className="rounded-lg cursor-pointer"
                    >
                      <span className="block max-w-[420px] truncate">
                        {cl.title}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 flex-col">
            <label
              htmlFor="tags"
              className="text-sm font-medium text-muted-foreground"
            >
              <LocalizedText id="legacy.tags.yjx284" />{" "}
              <Badge className="ml-2 text-muted-foreground" variant="outline">
                <LocalizedText id="legacy.soon.yjzi9k" />
              </Badge>{" "}
            </label>
            <MultiSelect
              className="w-full"
              options={[]}
              selected={selectedTags}
              disabled={true}
              onChange={(value) => {
                setAddPromptError("");
                setSelectedTags(value);
              }}
              placeholder={t("legacy.select.a.tag.9ob3ku")}
            >
              <Tags></Tags>
            </MultiSelect>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              onClick={() => {
                setPrompt("");
              }}
              variant="outline"
              className="cursor-pointer"
            >
              <LocalizedText id="legacy.cancel.18x3nqb" />
            </Button>
          </DialogClose>
          <Button
            type="submit"
            onClick={handlePromptAdd}
            className="cursor-pointer hover:bg-[#ff8138] text-white cursor-pointer bg-[#FF5E00]"
          >
            <LocalizedText id="legacy.add.prompt.g62pgg" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPromptsDialog;
